import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Endpoints } from '@/api';
import { useCurrentSession } from '@/services/auth';
import { useFetch } from '@/utils/fetch';

export interface UserRoleScope {
  organizationId?: string | null;
  entityId?: string | null;
}

export const useUserRole = ({ slug }: { slug: string }) => {
  const fetch = useFetch<Endpoints['GET /invite/current']>();

  return useQuery({
    queryKey: ['users', 'invite', 'current', slug],
    queryFn: () =>
      fetch(`/invite/current`, undefined, {
        headers: {
          'prolong-organization': slug,
        },
      }),
  });
};

export type InvitationFromGetMany = Endpoints['GET /invite']['response']['invitations'][number];

export const useInvitations = (
  params: Endpoints['GET /invite']['query'] = {},
  options?: {
    keepPreviousData?: boolean;
  }
) => {
  const fetch = useFetch<Endpoints['GET /invite']>();

  return useQuery({
    queryKey: ['users', 'invite', params],
    queryFn: () => fetch('/invite', params),
    placeholderData: options?.keepPreviousData ? keepPreviousData : undefined,
  });
};

export const useInviteUser = () => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /invite']>();

  return useMutation({
    mutationFn: (params: {
      email: string;
      userRoles: {
        id: string | null;
        roleId: string;
        organizationId: string | null;
        entityId: string | null;
      }[];
    }) =>
      fetch('/invite', undefined, {
        method: 'POST',
        body: params,
      }).then(() => {
        queryClient.invalidateQueries({ queryKey: ['users', 'invite'] });
      }),
  });
};

export const useAcceptInvitationAndLogin = () => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /invite/:email/accept-and-login']>();

  return useMutation({
    mutationFn: ({
      email,
      ...body
    }: {
      email: string;
      invitationToken: string;
      name: string;
      password?: string;
    }) =>
      fetch(`/invite/${email}/accept-and-login`, undefined, {
        method: 'POST',
        body,
      }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['users', 'invite'] });
    },
  });
};

export const useUpdateInvite = () => {
  const { currentSession } = useCurrentSession();
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['PATCH /invite/:userId']>();

  return useMutation({
    mutationFn: ({
      userId,
      ...body
    }: {
      userId: string;
      userRoles: {
        id: string | null;
        roleId: string;
        organizationId: string | null;
        entityId: string | null;
      }[];
    }) =>
      fetch(`/invite/${userId}`, undefined, {
        method: 'PATCH',
        body,
      }),
    onSettled: (_data, _error, variables) => {
      /**
       * If the current user updated their own roles, reload the page to
       * reflect the changes in the session immediately.
       */
      if (variables.userId === currentSession?.id) {
        location.reload();
      }

      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

export const useAcceptInvitation = () => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /invite/accept']>();

  return useMutation({
    mutationFn: ({ slug }: { slug: string }) =>
      fetch(`/invite/accept`, undefined, {
        method: 'POST',
        headers: {
          'prolong-organization': slug,
        },
      }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

export const useDeleteUserRole = () => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['DELETE /invite/:emailOrUserId']>();

  return useMutation({
    mutationFn: (params: { email: string } | { userId: string }) =>
      fetch(`/invite/${'email' in params ? params.email : params.userId}`, undefined, {
        method: 'DELETE',
      }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

export const useResendInvitation = () => {
  const fetch = useFetch<Endpoints['POST /invite/:email/resend-invitation']>();

  return useMutation({
    mutationFn: ({ email }: { email: string }) =>
      fetch(`/invite/${email}/resend-invitation`, undefined, {
        method: 'POST',
      }),
  });
};
